import { useEffect, useState } from 'react';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import type {
  LibraryCommercial,
  LibraryFormFactor,
  LibraryReliability,
  ModelDataType,
  ModelLibrary,
  ModelLibraryItem,
} from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { DATA_TYPE_LABEL_KEY, MODEL_DATA_TYPES, USE_CASE_NAME_KEY } from './catalog.js';

type Filter = ModelDataType | 'ALL';

/** Pastille de fiabilité : classe CSS + libellé. `unevaluated` tant qu'on n'a pas évalué chez soi. */
const RELIABILITY_META: Record<LibraryReliability, { cls: string; key: MessageKey }> = {
  high: { cls: 'ddp-pill--high', key: 'model.lib.reliability.high' },
  medium: { cls: 'ddp-pill--mid', key: 'model.lib.reliability.medium' },
  low: { cls: 'ddp-pill--low', key: 'model.lib.reliability.low' },
  unevaluated: { cls: 'ddp-pill--none', key: 'model.lib.reliability.unevaluated' },
};

const FORM_FACTOR_KEY: Record<LibraryFormFactor, MessageKey> = {
  embedded: 'model.lib.form.embedded',
  ground: 'model.lib.form.ground',
};

/** Droit d'usage commercial : libellé + intention visuelle (ok / avertissement / interdit). */
const COMMERCIAL_META: Record<LibraryCommercial, { cls: string; key: MessageKey }> = {
  yes: { cls: 'ok', key: 'model.lib.rights.commercialYes' },
  enterprise: { cls: 'warn', key: 'model.lib.rights.commercialEnterprise' },
  no: { cls: 'danger', key: 'model.lib.rights.commercialNo' },
};

/** Dernière obligation de licence à afficher, par ordre de gravité. */
function obligation(item: ModelLibraryItem): { cls: string; key: MessageKey } {
  if (item.license.copyleftNetwork) return { cls: 'warn', key: 'model.lib.rights.copyleft' };
  if (item.license.attribution) return { cls: 'neutral', key: 'model.lib.rights.attribution' };
  return { cls: 'neutral', key: 'model.lib.rights.none' };
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Section « Modèles » (ADR-0018) : la bibliothèque curée, servie par l'API et présentée en **fiche
 * métier** (ce qu'il détecte, données, gabarit, droits en clair), avec le détail technique replié et
 * une pastille de fiabilité honnête. Les métadonnées « vivantes » (téléchargements, mise à jour)
 * n'apparaissent que si la récupération souveraine est active côté serveur.
 */
export function ModelLibrarySection() {
  const t = useT();
  const [library, setLibrary] = useState<ModelLibrary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('ALL');

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiFetch<ModelLibrary>('/model-library');
        if (active) setLibrary(data);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : t('model.lib.loadError'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // Chargement au montage : `t` n'est qu'un message de repli, il ne doit pas redéclencher l'appel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = library?.items ?? [];
  const shown = filter === 'ALL' ? items : items.filter((model) => model.dataType === filter);

  return (
    <div className="ddp-lib">
      <p className="ddp-models__intro">{t('model.lib.intro')}</p>

      {/* Filtre par type de données — réutilise le motif « chips » de la page Data. */}
      <div className="ddp-sessions__list" role="group" aria-label={t('model.lib.objectsLabel')}>
        <button
          type="button"
          className={`ddp-sessions__chip${filter === 'ALL' ? ' is-selected' : ''}`}
          aria-pressed={filter === 'ALL'}
          onClick={() => setFilter('ALL')}
        >
          <span className="ddp-sessions__chip-name">{t('model.lib.filter.all')}</span>
        </button>
        {MODEL_DATA_TYPES.map((dataType) => (
          <button
            key={dataType}
            type="button"
            className={`ddp-sessions__chip${filter === dataType ? ' is-selected' : ''}`}
            aria-pressed={filter === dataType}
            onClick={() => setFilter(dataType)}
          >
            <span className="ddp-sessions__chip-name">{t(DATA_TYPE_LABEL_KEY[dataType])}</span>
          </button>
        ))}
      </div>

      {error && (
        <InlineNotification
          kind="error"
          title={t('model.notif.errorTitle')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      {loading ? (
        <Loading withOverlay={false} description={t('model.loading')} />
      ) : shown.length === 0 ? (
        <p className="ddp-models__empty">{t('model.lib.empty')}</p>
      ) : (
        <ul className="ddp-cards">
          {shown.map((model) => (
            <ModelCard key={model.id} model={model} />
          ))}
        </ul>
      )}

      {library && !library.liveEnabled && (
        <p className="ddp-models__hint">{t('model.lib.liveOff')}</p>
      )}
      <p className="ddp-models__hint">{t('model.lib.searchHint')}</p>
    </div>
  );
}

function ModelCard({ model }: { model: ModelLibraryItem }) {
  const t = useT();
  const rel = RELIABILITY_META[model.reliability];
  const commercial = COMMERCIAL_META[model.license.commercial];
  const oblig = obligation(model);

  return (
    <li className="ddp-modelcard">
      <div className="ddp-modelcard__top">
        <div>
          <h3 className="ddp-modelcard__name">{model.name}</h3>
          <span className="ddp-tag ddp-tag--type">{t(DATA_TYPE_LABEL_KEY[model.dataType])}</span>
        </div>
        <span
          className={`ddp-pill ${rel.cls}`}
          title={t(
            model.reliability === 'unevaluated'
              ? 'model.lib.reliabilityHint.unevaluated'
              : 'model.lib.reliabilityHint.measured',
          )}
        >
          <span className="ddp-pill__glyph" />
          {t(rel.key)}
        </span>
      </div>

      <div>
        <span className="ddp-modelcard__lbl">{t('model.lib.detectsLabel')}</span>
        <div className="ddp-chips">
          {model.useCaseIds.map((id) => {
            const nameKey = USE_CASE_NAME_KEY[id];
            return (
              <span key={id} className="ddp-chip">
                {nameKey ? t(nameKey) : id}
              </span>
            );
          })}
        </div>
      </div>

      <dl className="ddp-facts">
        <div>
          <dt>{t('model.lib.field.data')}</dt>
          <dd>{t(DATA_TYPE_LABEL_KEY[model.dataType])}</dd>
        </div>
        <div>
          <dt>{t('model.lib.field.form')}</dt>
          <dd>{t(FORM_FACTOR_KEY[model.formFactor])}</dd>
        </div>
        <div>
          <dt>{t('model.lib.field.speed')}</dt>
          <dd>{t(model.realtime ? 'model.lib.speed.realtime' : 'model.lib.speed.batch')}</dd>
        </div>
        <div>
          <dt>{t('model.lib.field.ready')}</dt>
          <dd className={model.onnxReady ? 'ddp-facts__ok' : undefined}>
            {t(model.onnxReady ? 'model.lib.ready.yes' : 'model.lib.ready.no')}
          </dd>
        </div>
      </dl>

      <div className="ddp-rights">
        <span className="ddp-modelcard__lbl">{t('model.lib.rightsLabel')}</span>
        <ul>
          <li className={`ddp-rights--${commercial.cls}`}>{t(commercial.key)}</li>
          <li className={`ddp-rights--${model.license.militaryAllowed ? 'ok' : 'danger'}`}>
            {t(
              model.license.militaryAllowed
                ? 'model.lib.rights.militaryYes'
                : 'model.lib.rights.militaryNo',
            )}
          </li>
          <li className={`ddp-rights--${oblig.cls}`}>{t(oblig.key)}</li>
        </ul>
      </div>

      <details className="ddp-tech">
        <summary>{t('model.lib.tech.title')}</summary>
        <dl>
          {model.params && (
            <>
              <dt>{t('model.lib.tech.params')}</dt>
              <dd>{model.params}</dd>
            </>
          )}
          <dt>{t('model.lib.tech.license')}</dt>
          <dd>{model.license.spdx}</dd>
          {model.live?.downloads != null && (
            <>
              <dt>{t('model.lib.tech.downloads')}</dt>
              <dd>{model.live.downloads.toLocaleString('fr-FR')}</dd>
            </>
          )}
          {model.live?.lastModified && (
            <>
              <dt>{t('model.lib.tech.updated')}</dt>
              <dd>{formatDate(model.live.lastModified)}</dd>
            </>
          )}
          {model.live?.reportedLicense && (
            <>
              <dt>{t('model.lib.tech.reported')}</dt>
              <dd>{model.live.reportedLicense}</dd>
            </>
          )}
          <dt>{t('model.lib.tech.source')}</dt>
          <dd>
            <a href={model.repoUrl} target="_blank" rel="noreferrer noopener">
              {model.platform} ↗
            </a>
          </dd>
        </dl>
      </details>
    </li>
  );
}
